import { ImagesInWallDecorMap } from '../../../../projects/zalamo-library/src/lib/ui/look-book-front/wall-decor-front/types';
import { WallDecorSceneForDisplay } from '../../wall-decor-front/src/graphql';
import { groupArrayByIndex } from '../../../../helpers/array';

export const wallDecorThumbRedrawNeeded = (
  current: ImagesInWallDecorMap,
  currentImages: string[],
): boolean => {
  return (
    Object.values(current).length !== currentImages.length ||
    Object.values(current).filter(
      ({ photoId }) => !currentImages.includes(photoId),
    ).length > 0
  );
};
export const triplexThumbRedrawNeeded = (
  current: ImagesInWallDecorMap,
  currentImages: string[],
  availableImages: Record<
    string,
    { photoId: string; width: number; height: number }
  >,
): boolean => {
  if (currentImages.length < 3) {
    return false;
  }
  const photoChanged =
    Object.values(current).length !== Math.min(currentImages.length, 3) ||
    Object.values(current).filter(
      ({ photoId }) => !currentImages.includes(photoId),
    ).length > 0;

  // const areCurrentNotOnlyHorizontal = Object.values(current).find(
  //   (v) => availableImages[v.photoId].width > availableImages[v.photoId].height,
  // );
  // const verticalOnlyAvailable =
  //   Object.values(availableImages).filter(
  //     ({ height, width, photoId }) =>
  //       currentImages.includes(photoId) && height > width,
  //   ).length >= 3;

  return photoChanged;
};
export const pickImagesForThumb = (
  images: { photoId: string; width: number; height: number }[],
  scenes: WallDecorSceneForDisplay[],
  imagesCount = 3,
) => {
  const matchingScenes = scenes.filter(
    ({ pieces }) => pieces.length === imagesCount,
  );
  return findBestLayoutForWallDecor(matchingScenes, images);
};

export const pickImagesForTriplexThumb = (
  images: { photoId: string; width: number; height: number }[],
): {
  images: { photoId: string; width: number; height: number }[];
  isVertical: boolean;
} => {
  const verticals = images.filter(({ width, height }) => height > width);
  if (verticals.length >= 3) {
    return { images: verticals.splice(0, 3), isVertical: true };
  }
  return {
    images: verticals.concat(
      images.filter(
        ({ photoId }) =>
          !verticals.map(({ photoId }) => photoId).includes(photoId),
      ),
    ),
    isVertical: verticals.length >= 2,
  };
};

export function findBestLayoutForWallDecor(
  scenes: WallDecorSceneForDisplay[] & {
    diff?: number;
  },
  photos: { photoId: string; width: number; height: number }[],
): {
  matchingScene: WallDecorSceneForDisplay;
  pieces: Record<
    string,
    {
      WallDecorPieceForDisplayId: string;
      photoId: string;
      isFlip: boolean;
    }
  >;
} {
  let photosRatios = photos.map((photo) => ({
    photoId: photo.photoId,
    ratio: photo.width / photo.height,
  }));
  const sortedScenes = scenes.map((config) => {
    let tempPhotos = [...photosRatios];

    return {
      ...config,
      diff: config.pieces.reduce((previousValue, currentValue) => {
        const currentModelRatio = currentValue.flip
          ? currentValue.imageHeight / currentValue.imageWidth
          : currentValue.imageWidth / currentValue.imageHeight;
        const bestMatchingImage = tempPhotos.sort(
          (a, b) =>
            Math.abs(a.ratio - currentModelRatio) -
            Math.abs(b.ratio - currentModelRatio),
        );
        tempPhotos = tempPhotos.filter(
          ({ photoId }) => photoId !== bestMatchingImage[0].photoId,
        );
        return (
          previousValue +
          Math.abs(currentModelRatio - bestMatchingImage[0]?.ratio) +
          (Math.abs(currentModelRatio - bestMatchingImage[0]?.ratio) > 0.2
            ? 1
            : 0)
        );
      }, 0),
    };
  });
  const sorted = sortedScenes.sort((a, b) => a.diff - b.diff);
  const first = sorted[0];
  const bestScenes = sorted.filter((scene) => scene.diff - first.diff < 0.1);
  const matchingScene = bestScenes.sort(
    (a, b) =>
      b.pieces.reduce(
        (previousValue, currentValue) =>
          previousValue + currentValue.imageHeight * currentValue.imageHeight,
        0,
      ) -
      a.pieces.reduce(
        (previousValue, currentValue) =>
          previousValue + currentValue.imageHeight * currentValue.imageHeight,
        0,
      ),
  )[0];
  return {
    matchingScene,
    pieces: matchingScene
      ? groupArrayByIndex(
          matchingScene.pieces.map(
            ({ WallDecorPieceForDisplayId, imageWidth, imageHeight, flip }) => {
              const currentModelRatio = flip
                ? imageHeight / imageWidth
                : imageWidth / imageHeight;
              const bestMatchingImage = photosRatios.sort(
                (a, b) =>
                  Math.abs(a.ratio - currentModelRatio) -
                  Math.abs(b.ratio - currentModelRatio),
              );
              photosRatios = photosRatios.filter(
                (p) => p.photoId !== bestMatchingImage[0].photoId,
              );
              return {
                WallDecorPieceForDisplayId,
                photoId: bestMatchingImage[0].photoId,
                isFlip: flip,
              };
            },
          ),
          'WallDecorPieceForDisplayId',
        )
      : null,
  };
}
